import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compact, inRange, ceil } from 'lodash';
import Container from 'reactstrap/lib/Container';
import { Carousel } from 'site-modules/shared/components/carousel/carousel';
import { breakpoints } from 'client/utils/bootstrap';
import { INVENTORY_TYPES_LOWERCASE } from 'client/constants/inventory-types';
import { getPlural } from 'client/utils/plural';
import { getDealerDetailsSearchResultsUrl } from 'site-modules/shared/utils/dealerships/dealer-inventory';
import { DealerDetailsSearchResultsElement } from 'site-modules/dealerships/components/dealer-details-search-results-element/dealer-details-search-results-element';
import { DealerInventoryCarouselItem } from 'site-modules/dealerships/components/dealer-inventory-carousel/dealer-inventory-carousel-item';
import { Link } from 'site-modules/shared/components/link/link';
import { NextArrow, PrevArrow } from './dealer-carousel-arrows';

import './dealer-inventory-carousel.scss';

const SLIDES_TO_SHOW_MOBILE = 1.5;
const SLIDES_TO_SCROLL_MOBILE = 1;

const SLIDES_TO_SHOW_NON_MOBILE_ABOVE_XL = 4;
const SLIDES_TO_SHOW_NON_MOBILE_BETWEEN_MD_XL = 3;
const SLIDES_TO_SHOW_NON_MOBILE_BETWEEN_SM_MD = 2;
const SLIDES_TO_SHOW_NON_MOBILE_BELOW_SM = 1;

const FIRST_SLIDE_NUMBER = 0;

function DealerInventoryCarouselUI({ makeModels, isMobile }) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(FIRST_SLIDE_NUMBER);
  const carouselRef = useRef();
  const carsCount = makeModels.rooftopCounters;
  const { NEW, USED, CPO } = INVENTORY_TYPES_LOWERCASE;

  const SHARED_CAROUSEL_OPTIONS = {
    className: 'page-counter-bold',
    // pixel tracking
    contentDetails: 'inventory_details',
    customEventData: {
      partner_id: makeModels.rooftopId,
    },
  };

  const carouselOptions = isMobile
    ? {
        ...SHARED_CAROUSEL_OPTIONS,
        slidesToShow: SLIDES_TO_SHOW_MOBILE,
        slidesToScroll: SLIDES_TO_SCROLL_MOBILE,
        infinite: false,
        arrows: false,
      }
    : {
        ...SHARED_CAROUSEL_OPTIONS,
        // above xl breakpoint
        slidesToShow: SLIDES_TO_SHOW_NON_MOBILE_ABOVE_XL,
        slidesToScroll: SLIDES_TO_SHOW_NON_MOBILE_ABOVE_XL,
        infinite: false,
        arrows: makeModels.models.length > SLIDES_TO_SHOW_NON_MOBILE_ABOVE_XL,
        responsive: [
          {
            breakpoint: breakpoints.xl, // between md and xl breakpoints
            settings: {
              slidesToShow: SLIDES_TO_SHOW_NON_MOBILE_BETWEEN_MD_XL,
              slidesToScroll: SLIDES_TO_SHOW_NON_MOBILE_BETWEEN_MD_XL,
              arrows: makeModels.models.length > SLIDES_TO_SHOW_NON_MOBILE_BETWEEN_MD_XL,
            },
          },
          {
            breakpoint: breakpoints.md, // between sm and md breakpoints
            settings: {
              slidesToShow: SLIDES_TO_SHOW_NON_MOBILE_BETWEEN_SM_MD,
              slidesToScroll: SLIDES_TO_SHOW_NON_MOBILE_BETWEEN_SM_MD,
              arrows: makeModels.models.length > SLIDES_TO_SHOW_NON_MOBILE_BETWEEN_SM_MD,
            },
          },
          {
            breakpoint: breakpoints.sm, // below sm breakpoints
            settings: {
              slidesToShow: SLIDES_TO_SHOW_NON_MOBILE_BELOW_SM,
              slidesToScroll: SLIDES_TO_SHOW_NON_MOBILE_BELOW_SM,
              arrows: makeModels.models.length > SLIDES_TO_SHOW_NON_MOBILE_BELOW_SM,
            },
          },
        ],
      };

  const onPrevClick = useCallback(() => {
    carouselRef?.current?.prev();
  }, []);

  const onNextClick = useCallback(() => {
    carouselRef?.current?.next();
  }, []);

  const beforeSlideChange = useCallback((from, to) => {
    setCurrentSlideIndex(to);
  }, []);

  // unfortunately we don't get access to the carouselRef until the slider is interacted with (without forcing a
  // repaint) so we need to pick the best fallback values
  const slidesToShow =
    carouselRef?.current?.slider?.innerSlider?.props?.slidesToShow ||
    (isMobile ? SLIDES_TO_SHOW_MOBILE : SLIDES_TO_SHOW_NON_MOBILE_BETWEEN_MD_XL);

  const isPrevDisabled = currentSlideIndex === FIRST_SLIDE_NUMBER;
  const isNextDisabled = currentSlideIndex >= makeModels.models.length - slidesToShow;

  const showBottomArrows = isMobile ? makeModels.models.length > SLIDES_TO_SCROLL_MOBILE : false;

  const links = compact([
    carsCount[NEW] > 0 && (
      <DealerDetailsSearchResultsElement
        locationId={makeModels.rooftopId}
        inventoryType={NEW}
        dataTrackingValue={NEW}
        className="text-underline"
      >
        {carsCount[NEW]} {NEW}
      </DealerDetailsSearchResultsElement>
    ),
    carsCount[USED] > 0 && (
      <DealerDetailsSearchResultsElement
        locationId={makeModels.rooftopId}
        inventoryType={USED}
        dataTrackingValue={USED}
        className="text-underline"
      >
        {carsCount[USED]} {USED}
      </DealerDetailsSearchResultsElement>
    ),
    carsCount[CPO] > 0 && (
      <DealerDetailsSearchResultsElement
        locationId={makeModels.rooftopId}
        inventoryType={CPO}
        dataTrackingValue={CPO}
        className="text-underline"
      >
        {carsCount[CPO]} certified pre-owned
      </DealerDetailsSearchResultsElement>
    ),
  ]);

  return (
    <section className="dealer-inventory-carousel bg-blue-90 overlay-white-40">
      <Container className="dealer-carousel-container py-3 px-xl-0" id="edm-entry-dealer-inventory" fluid>
        <div className="px-0_5">
          <h2 className="size-30 text-center text-gray-darkest mb-1">
            <DealerDetailsSearchResultsElement
              locationId={makeModels.rooftopId}
              dataTrackingValue="all"
              isLink={carsCount.total > 0}
              className="text-decoration-none"
            >
              <span className="text-blue-30 text-underline">{carsCount.total}</span> {getPlural('car', carsCount.total)}{' '}
              in stock
            </DealerDetailsSearchResultsElement>
          </h2>
          <ul className="list-unstyled text-cool-gray-30 d-flex justify-content-center mb-2">
            {!!links.length &&
              links.map((link, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`cars-in-stock-link-${index}`}>
                  {!!index && (
                    <span className="px-0_5" aria-hidden>
                      &bull;
                    </span>
                  )}
                  {link}
                </li>
              ))}
          </ul>
        </div>
        {!!makeModels.models.length && (
          <div className="dealer-carousel-wrapper mx-auto">
            <Carousel
              {...carouselOptions}
              ref={carouselRef}
              beforeChange={beforeSlideChange}
              fluid={isMobile}
              prevArrow={<PrevArrow disabled={isPrevDisabled} />}
              nextArrow={<NextArrow disabled={isNextDisabled} />}
            >
              {makeModels.models.map((makeModel, index) => {
                const isVisible = inRange(index, currentSlideIndex, ceil(currentSlideIndex + slidesToShow));
                return (
                  <div key={makeModel.make + makeModel.model}>
                    <DealerInventoryCarouselItem
                      makeModel={makeModel}
                      locationId={makeModels.rooftopId}
                      isHidden={!isVisible}
                    />
                  </div>
                );
              })}
            </Carousel>
          </div>
        )}
        {showBottomArrows && (
          <div className="d-flex justify-content-center align-items-center mt-2 hidden-xl-up">
            <PrevArrow disabled={isPrevDisabled} onClick={onPrevClick} />
            <NextArrow disabled={isNextDisabled} onClick={onNextClick} />
          </div>
        )}
        <div className="text-center mt-2">
          <Link
            to={getDealerDetailsSearchResultsUrl(makeModels.rooftopId)}
            className="text-transform-none fw-normal p-0"
            data-tracking-id="ddp_view_search_result"
            data-tracking-value="all"
          >
            <span className="text-underline">Show all inventory</span>
            <span className="icon-arrow-right3 size-12 ms-0_5" aria-hidden />
          </Link>
        </div>
      </Container>
    </section>
  );
}

DealerInventoryCarouselUI.propTypes = {
  makeModels: PropTypes.shape({
    rooftopId: PropTypes.number,
    rooftopCounters: PropTypes.shape({
      new: PropTypes.number,
      cpo: PropTypes.number,
      used: PropTypes.number,
    }).isRequired,
    models: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
  isMobile: PropTypes.bool.isRequired,
};

DealerInventoryCarouselUI.defaultProps = {
  makeModels: {
    rooftopCounters: {
      new: 0,
      cpo: 0,
      used: 0,
    },
  },
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const DealerInventoryCarousel = connect(mapStateToProps)(DealerInventoryCarouselUI);
